import React, { useEffect, useState, useCallback } from "react"
import { Row, Col, Card, CardBody, Button, Spinner } from "reactstrap"
import { connect } from "react-redux"
import { utils, writeFile } from "xlsx"
import { useNavigate } from "react-router-dom"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"
import "./Empresas.scss"

const ConsultarEmpresas = ({ setBreadcrumbItems, user, empresas: reduxEmpresas }) => {
  document.title = "Consultar Empresas | LS-DA - Softlogy"

  const [empresas, setEmpresas] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtroEstado, setFiltroEstado] = useState("activo") // activo | inactivo | todos
  const [reactivando, setReactivando] = useState(null)
  const navigate = useNavigate()

  // Only SuperAdmin or admin of alianza can create empresas
  const [canCreateEmpresas, setCanCreateEmpresas] = useState(
    user?.esSuperAdmin || user?.esAdminAlianza || false
  )

  // Breadcrumb
  useEffect(() => {
    const greeting = user
      ? `Bienvenido, ${user.nombre} ${user.apellido}`
      : "Dashboard"
    setBreadcrumbItems(greeting, [
      { title: "LS-DA", link: "#" },
      { title: "Empresas", link: "#" },
      { title: "Consultar Empresas", link: "#" },
    ])
  }, [setBreadcrumbItems, user])

  // Fetch empresas
  const fetchEmpresas = useCallback(async () => {
    setLoading(true)
    try {
      const endpoint = user?.esSuperAdmin ? "empresas" : "empresas/mis-empresas"
      // "activo" es el default del back (sin param); "inactivo" y "todos" van explícitos
      const params = filtroEstado !== "activo" ? { estado: filtroEstado } : {}
      const response = await axiosClient.get(endpoint, { params })
      const data = response.data.data || response.data
      // Normalizar: mis-empresas puede devolver [{ empresa: {...}, esAdmin: bool }]
      // o directamente [{ id, nombre, ... }]
      const list = (Array.isArray(data) ? data : []).map(item =>
        item?.empresa ? { ...item.empresa, esAdminEmpresa: item.esAdminEmpresa } : item
      )
      setEmpresas(list)

      // Habilitar crear empresa si alguna es alianza
      if (!canCreateEmpresas && list.some(e => e.esAlianza === true)) {
        setCanCreateEmpresas(true)
      }
    } catch (error) {
      console.error("Error al obtener empresas:", error)
    } finally {
      setLoading(false)
    }
  }, [user, canCreateEmpresas, filtroEstado])

  useEffect(() => {
    fetchEmpresas()
  }, [fetchEmpresas])

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = empresas.map(e => ({
      Nombre: e.nombre || "",
      NIT: e.nit || "",
      "Dígito Verificación": e.digVerificacion || "",
      Email: e.email || "",
      Teléfono: e.telefono || "",
      Dirección: e.direccion || "",
      Ciudad: e.ciudad || "",
      Estado: e.activo !== false ? "Activo" : "Inactivo",
    }))
    const worksheet = utils.json_to_sheet(exportData)
    const workbook = utils.book_new()
    utils.book_append_sheet(workbook, worksheet, "Empresas")
    writeFile(workbook, "Empresas.xlsx")
  }

  const handleModificar = (id) => {
    navigate(`/portal/empresa/${id}`)
  }

  const handleSucursales = (id) => {
    navigate(`/portal/sucursales/${id}`)
  }

  const handleResoluciones = (id) => {
    navigate(`/portal/resoluciones/${id}`)
  }

  // Reactivar una empresa inactiva
  const handleReactivar = async (id) => {
    if (reactivando) return
    setReactivando(id)
    try {
      await axiosClient.put(`empresas/${id}`, { activo: true })
      await fetchEmpresas()
    } catch (error) {
      console.error("Error al reactivar empresa:", error)
      alert("No se pudo reactivar la empresa. Intenta de nuevo.")
    } finally {
      setReactivando(null)
    }
  }

  // Filter
  const filteredEmpresas = searchTerm
    ? empresas.filter(e =>
      e.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.nit?.includes(searchTerm)
    )
    : empresas

  // Pagination
  const totalPages = Math.ceil(filteredEmpresas.length / pageSize)
  const startIdx = (currentPage - 1) * pageSize
  const paginatedEmpresas = filteredEmpresas.slice(startIdx, startIdx + pageSize)

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const thStyle = {
    fontWeight: 600, fontSize: "0.8rem", color: "#64748b",
    textTransform: "uppercase", letterSpacing: "0.5px",
  }

  return (
    <React.Fragment>
      <Row>
        <Col className="col-12">
          <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <CardBody style={{ padding: "2rem" }}>
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                  <h4 className="mb-1" style={{ fontWeight: 700 }}>Consultar Empresas</h4>
                  <small className="text-muted">
                    {user?.esSuperAdmin
                      ? "Todas las empresas del sistema"
                      : "Empresas asignadas a tu cuenta"}
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    style={{ backgroundColor: "#007ea7", color: "white", border: "none", borderRadius: 8 }}
                    onClick={handleExportToExcel}
                    disabled={empresas.length === 0}
                  >
                    <i className="mdi mdi-file-excel-outline me-1" /> Exportar Excel
                  </Button>
                  {canCreateEmpresas && (
                    <Button
                      color="primary"
                      onClick={() => navigate("/portal/empresa/crear")}
                      style={{ borderRadius: 8 }}
                    >
                      <i className="mdi mdi-plus me-1" /> Crear Empresa
                    </Button>
                  )}
                </div>
              </div>

              {/* Tabs de estado */}
              <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 10, padding: 3, marginBottom: 20, gap: 3 }}>
                {[
                  { v: "activo", l: "Activas", icon: "mdi-check-circle-outline" },
                  { v: "inactivo", l: "Inactivas", icon: "mdi-cancel" },
                  { v: "todos", l: "Todas", icon: "mdi-format-list-bulleted" },
                ].map(t => {
                  const isActive = filtroEstado === t.v
                  return (
                    <button
                      key={t.v}
                      type="button"
                      onClick={() => { setFiltroEstado(t.v); setCurrentPage(1) }}
                      style={{
                        border: "none", borderRadius: 8, padding: "7px 16px",
                        fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
                        background: isActive ? "#fff" : "transparent",
                        color: isActive ? "#0f172a" : "#64748b",
                        boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                        display: "flex", alignItems: "center", gap: 6, transition: "all .15s",
                      }}
                    >
                      <i className={`mdi ${t.icon}`} />{t.l}
                    </button>
                  )
                })}
              </div>

              {/* Search */}
              <div style={{ position: "relative", marginBottom: 20 }}>
                <i className="mdi mdi-magnify" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 18 }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre o NIT..."
                  style={{
                    width: "100%", padding: "10px 16px 10px 42px",
                    border: "1px solid #e2e8f0", borderRadius: 10,
                    fontSize: "0.9rem", outline: "none", backgroundColor: "#f8fafc",
                  }}
                  onFocus={e => { e.target.style.borderColor = "#556ee6" }}
                  onBlur={e => { e.target.style.borderColor = "#e2e8f0" }}
                />
              </div>

              {/* Loading */}
              {loading && (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Cargando empresas...</p>
                </div>
              )}

              {/* Table */}
              {!loading && filteredEmpresas.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr style={{ backgroundColor: "#f8fafc" }}>
                        <th style={thStyle}>Empresa</th>
                        <th style={thStyle}>NIT</th>
                        <th style={thStyle}>Email</th>
                        <th style={thStyle}>Teléfono</th>
                        <th style={thStyle}>Estado</th>
                        <th style={{ ...thStyle, width: 360 }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEmpresas.map(empresa => (
                        <tr key={empresa.id || empresa.nit}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {empresa.logoUrl ? (
                                <img
                                  src={empresa.logoUrl} alt=""
                                  style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 6 }}
                                  onError={e => { e.target.style.display = "none" }}
                                />
                              ) : (
                                <div style={{
                                  width: 32, height: 32, borderRadius: 6,
                                  backgroundColor: "#556ee6", display: "flex",
                                  alignItems: "center", justifyContent: "center",
                                  color: "#fff", fontWeight: 700, fontSize: 14,
                                }}>
                                  {empresa.nombre?.charAt(0)?.toUpperCase() || "E"}
                                </div>
                              )}
                              <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                {empresa.nombre}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span style={{ fontSize: "0.85rem", color: "#475569" }}>
                              {empresa.nit}{empresa.digVerificacion ? `-${empresa.digVerificacion}` : ""}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: "0.85rem", color: "#475569" }}>
                              {empresa.email || "—"}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: "0.85rem", color: "#475569" }}>
                              {empresa.telefono || "—"}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              fontSize: "0.75rem", fontWeight: 600, padding: "4px 10px",
                              borderRadius: 20,
                              backgroundColor: empresa.activo !== false ? "#d1fae5" : "#fee2e2",
                              color: empresa.activo !== false ? "#059669" : "#dc3545",
                            }}>
                              {empresa.activo !== false ? "Activo" : "Inactivo"}
                            </span>
                          </td>
                          <td>
                            {empresa.activo === false && (user?.esSuperAdmin || user?.esAdminAlianza || empresa.esAdminEmpresa) && (
                              <Button color="success" size="sm" className="me-1"
                                onClick={() => handleReactivar(empresa.id)}
                                disabled={reactivando === empresa.id}
                                style={{ borderRadius: 6, fontSize: "0.78rem" }}>
                                {reactivando === empresa.id
                                  ? <><Spinner size="sm" /> …</>
                                  : <><i className="mdi mdi-restore me-1" />Reactivar</>}
                              </Button>
                            )}
                            <Button color="outline-warning" size="sm" className="me-1"
                              onClick={() => handleModificar(empresa.id)}
                              style={{ borderRadius: 6, fontSize: "0.78rem" }}>
                              <i className="mdi mdi-pencil-outline me-1" />Modificar
                            </Button>
                            <Button color="outline-primary" size="sm" className="me-1"
                              onClick={() => handleSucursales(empresa.id)}
                              style={{ borderRadius: 6, fontSize: "0.78rem" }}>
                              <i className="mdi mdi-store-outline me-1" />Sucursales
                            </Button>
                            <Button color="outline-info" size="sm"
                              onClick={() => handleResoluciones(empresa.id)}
                              style={{ borderRadius: 6, fontSize: "0.78rem" }}>
                              <i className="mdi mdi-file-document-outline me-1" />Resoluciones
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Empty */}
              {!loading && filteredEmpresas.length === 0 && (
                <div className="text-center py-5">
                  <i className="mdi mdi-office-building-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
                  <p className="text-muted mt-2 mb-0">
                    {searchTerm ? "No se encontraron empresas con ese criterio" : "No hay empresas disponibles"}
                  </p>
                </div>
              )}

              {/* Pagination & Count */}
              {!loading && filteredEmpresas.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">
                      Mostrando {startIdx + 1}–{Math.min(startIdx + pageSize, filteredEmpresas.length)} de {filteredEmpresas.length} empresa{filteredEmpresas.length !== 1 ? "s" : ""}
                    </small>
                    <select
                      value={pageSize}
                      onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
                      style={{ padding: "2px 8px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: "0.8rem", outline: "none" }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <Button color="outline-secondary" size="sm" disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)} style={{ borderRadius: 6, padding: "4px 8px" }}>
                      <i className="mdi mdi-chevron-double-left" />
                    </Button>
                    <Button color="outline-secondary" size="sm" disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)} style={{ borderRadius: 6, padding: "4px 8px" }}>
                      <i className="mdi mdi-chevron-left" />
                    </Button>
                    <span style={{ fontSize: "0.85rem", fontWeight: 600, padding: "0 10px", color: "#334155" }}>
                      {currentPage} / {totalPages}
                    </span>
                    <Button color="outline-secondary" size="sm" disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)} style={{ borderRadius: 6, padding: "4px 8px" }}>
                      <i className="mdi mdi-chevron-right" />
                    </Button>
                    <Button color="outline-secondary" size="sm" disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)} style={{ borderRadius: 6, padding: "4px 8px" }}>
                      <i className="mdi mdi-chevron-double-right" />
                    </Button>
                    <Button color="outline-secondary" size="sm" onClick={fetchEmpresas} style={{ borderRadius: 6, padding: "4px 8px", marginLeft: 8 }}>
                      <i className="mdi mdi-refresh" />
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  user: state.Login.user,
  empresas: state.Login.empresas,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(ConsultarEmpresas)
